/*
  Six words for the top of a table row.

  The row leads with the owner's own first sentence, which is the honest thing to show and
  the wrong thing to lead with. Sentences are as long as they are: "You said you would: Call
  all three departed techs and produce a one-page written summary" runs the width of the
  card and truncates, and a table of six of those is six truncated sentences that all start
  the same way. There is nothing to scan.

  A short label above the sentence gives the eye something to land on, next to the mark that
  already says which species of problem it is. The sentence stays underneath, so nothing is
  lost: this adds a heading, it does not replace the content.

  ── Why not a model ─────────────────────────────────────────────────────────

  The same reason as topics.ts. A model would write a better label and would mean a column,
  a migration, a worker change and a backfill, with every item already on every table
  unlabelled until it ran. This works on the whole history the moment it ships and is
  replaceable later without a caller changing.

  ── The rule ────────────────────────────────────────────────────────────────

  It must never say something the owner did not. Every word here comes out of their own
  sentence in their own order; nothing is invented, inferred or rephrased. The worst case is
  a label that is merely unhelpful, never one that is wrong.
*/

/**
 * Openers that carry no information about the item. Stripped so the six words are spent on
 * the subject rather than on the throat-clearing in front of it.
 *
 * The first group is the board's own follow-up template, which is generated in
 * CheckCommitmentsJobHandler and is identical on every one of them.
 */
const PREFIXES: RegExp[] = [
  /^you said you would:?\s*/i,
  /^we agreed success looks like:?\s*/i,
  /^(hey|hi|ok|okay|so|well)[,!.]?\s+/i,
  /^(i\s+(just\s+)?(want|need|would like)\s+to)\s+/i,
  /^(i'?m|i am)\s+(thinking about|wondering|considering)\s+/i,
  /^(should|shall|could|can|do|does|did|would|will)\s+(i|we|you)\s+/i,
  /^what (should|do) (i|we) do about\s+/i,
  /^(what|how|why|when|where)\s+(about|is|are|do|does|should)\s+/i,
  /^(a\s+)?question about\s+/i,
  /^(the\s+)?board:?\s*/i,
]

/** Words not worth spending the budget on when they land at the very start. */
const LEADING_FILLER = new Set(['the', 'a', 'an', 'my', 'our', 'is', 'are', 'was', 'were', 'to'])

/*
  A line budget, measured, rather than a word count.

  Six words was the first rule and it counted the wrong thing. Words vary in width, so six of
  them landed anywhere between "Raise" and "Report two capacity numbers: (a) overtime", and
  the cut fell mid-phrase as often as not. What the screen actually cares about is how many
  lines the label takes.

  The ceiling is measured. The binding surface is the Table row's title, which shares its
  baseline with the date and the Open toggle and so gets less width than the rail card's
  label: 308px at a 390px viewport, where 44 characters is the last cut that still sets in
  two lines.

  40 rather than that 44, because the ceiling is not the best value under it. The trailing
  trim below is what makes a cut read as a label rather than as a severed sentence, and it
  only fires when the cut lands on a function word. Four characters of headroom is the
  difference between "Complete the injury response for the scalded" and "Complete the injury
  response", and between "Call all three departed techs and produce" and "Call all three
  departed techs". The shorter one is not a compromise, it is the better label.

  It is a budget and not a guarantee. A 320px phone, and any phone with iOS Dynamic Type
  turned up, will overrun it, which is why both surfaces also carry a two-line clamp in CSS.
  This keeps the ellipsis rare; the clamp is what keeps the promise.
*/
const MAX_CHARS = 40

/**
 * A short label for an item, taken from its own words.
 *
 * Returns an empty string when the text yields nothing worth showing, which the caller
 * should treat as "draw no label" rather than drawing an empty one.
 */
export function summarise(text: string | null | undefined): string {
  if (!text) return ''

  let s = text.replace(/\s+/g, ' ').trim()

  // The first sentence, before any trimming, so a prefix at the start of sentence two is
  // not mistaken for the start of the item.
  const stop = s.search(/[.?!](\s|$)/)
  if (stop > 0) s = s.slice(0, stop)

  // Repeatedly, because the board's template stacks two of them.
  for (let pass = 0; pass < 3; pass++) {
    const before = s
    for (const p of PREFIXES) s = s.replace(p, '')
    s = s.trim()
    if (s === before) break
  }

  let words = s.split(' ').filter(Boolean)
  while (words.length > 1 && LEADING_FILLER.has(words[0]!.toLowerCase())) words = words.slice(1)
  if (words.length === 0) return ''

  /*
    Whole words up to the budget. Never a mid-word cut: "Complete the injury respo" reads as
    damage rather than as an abbreviation, and the one thing this function promises is that
    every word in the label is a word the owner wrote.

    The first word is always kept even when it alone overruns, so a single long word still
    yields a label instead of nothing. The clamp deals with the overflow.
  */
  const kept: string[] = []
  for (const word of words) {
    const length = kept.length === 0 ? word.length : kept.join(' ').length + 1 + word.length
    if (kept.length > 0 && length > MAX_CHARS) break
    kept.push(word)
  }

  /*
    A label ending on "for a" or "this month on the" reads as a sentence someone cut off,
    because it is one. Dangling function words come off the end until the label ends on
    something that carries meaning. Repeatedly, since trimming "a" usually exposes the "for"
    that was in front of it.
  */
  const DANGLING =
    /^(and|or|but|so|because|that|which|who|with|for|of|in|on|at|to|into|from|by|as|than|then|a|an|the|my|our|your|its|their|this|these|those|is|are|was|were|be|been|about|before|after|over|under|above|below|per|near|upon|via|when|while|if)$/i

  /*
    A bare enumerator is the other way a cut strands nothing. "Report two capacity numbers:
    (a)" spends four characters promising a list and then does not start it, which is worse
    than stopping at "numbers". Matches "(a)", "a)", "(1)" and "1.", never a real word.
  */
  const ENUMERATOR = /^\(?[a-z0-9]\)$|^[a-z0-9][.)]$/i

  while (
    kept.length > 1
    && (DANGLING.test(kept[kept.length - 1]!) || ENUMERATOR.test(kept[kept.length - 1]!))
  ) kept.pop()

  const label = kept.join(' ').replace(/[,;:]+$/, '')
  if (!label) return ''

  return label.charAt(0).toUpperCase() + label.slice(1)
}
